"use client"

import { useState, useEffect, useContext } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import axios from "axios"
import { API_URL } from "../config"
import { AuthContext } from "../context/AuthContext"
import "./VerifyEmail.css"

const VerifyEmail = () => {
  const { token } = useParams()
  const navigate = useNavigate()
  const { login } = useContext(AuthContext)

  const [status, setStatus] = useState("verifying") // verifying, success, error
  const [message, setMessage] = useState("")
  const [resendEmail, setResendEmail] = useState("")
  const [resendLoading, setResendLoading] = useState(false)

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/auth/verify-email/${token}`)

        if (response.data.token) {
          // Auto-login the user
          localStorage.setItem("token", response.data.token)

          // Update auth context
          await login(response.data.token)

          setStatus("success")
          setMessage("Email verified successfully! Redirecting to your profile...")

          // Redirect to profile page after 3 seconds
          setTimeout(() => {
            navigate("/profile")
          }, 3000)
        }
      } catch (err) {
        setStatus("error")
        setMessage(err.response?.data?.msg || "Email verification failed")
      }
    }

    if (token) {
      verifyEmail()
    }
  }, [token, login, navigate])

  const handleResendVerification = async (e) => {
    e.preventDefault()

    if (!resendEmail) {
      setMessage("Please enter your email address")
      return
    }

    setResendLoading(true)

    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, { email: resendEmail })
      setMessage("Verification email sent successfully! Please check your inbox.")
    } catch (err) {
      setMessage(err.response?.data?.msg || "Failed to send verification email")
    } finally {
      setResendLoading(false)
    }
  }

  return (
    <div className="verify-email-container">
      <div className="verify-email-card">
        <div className="verify-email-header">
          <i
            className={`fas ${status === "verifying" ? "fa-spinner fa-spin" : status === "success" ? "fa-check-circle" : "fa-exclamation-circle"}`}
          ></i>
          <h1>Email Verification</h1>
        </div>

        <div className="verify-email-content">
          {status === "verifying" && (
            <div className="verifying-state">
              <p>Verifying your email address...</p>
              <div className="spinner"></div>
            </div>
          )}

          {status === "success" && (
            <div className="success-state">
              <p className="success-message">{message}</p>
              <div className="success-actions">
                <Link to="/profile" className="profile-button">
                  Go to Profile
                </Link>
                <Link to="/dashboard" className="dashboard-button">
                  Go to Dashboard
                </Link>
              </div>
            </div>
          )}

          {status === "error" && (
            <div className="error-state">
              <p className="error-message">{message}</p>

              <div className="resend-section">
                <h3>Need a new verification link?</h3>
                <form onSubmit={handleResendVerification} className="resend-form">
                  <div className="form-group">
                    <label htmlFor="resendEmail">Email Address</label>
                    <input
                      type="email"
                      id="resendEmail"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      placeholder="Enter your email address"
                      required
                    />
                  </div>
                  <button type="submit" disabled={resendLoading} className="resend-button">
                    {resendLoading ? "Sending..." : "Resend Verification Email"}
                  </button>
                </form>
              </div>

              <div className="help-links">
                <Link to="/login">Back to Login</Link>
                <Link to="/register">Create New Account</Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default VerifyEmail
